import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar, Clock, MapPin, User, Search, Download, Plus, AlertCircle, CheckCircle, XCircle, Eye, ThumbsUp, ThumbsDown, Copy, ExternalLink } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { BookingsListLite } from '../components/lite';
import { toast as sonnerToast } from '../components/ui/sonner';

// ── Normalizers for mixed Booking / booking_request shapes ──────────────────
const getCustomerName  = (b) => b?.customer_name  || b?.customer?.name  || 'N/A';
const getCustomerEmail = (b) => b?.customer_email || b?.customer?.email || '';
const getServiceName   = (b) => b?.listing_title  || b?.service?.name   || 'N/A';
const getBookingDate   = (b) => b?.event_date      || b?.schedule?.date  || null;
const getBookingTime   = (b) => b?.schedule?.time  || null;
const getAmount        = (b) => b?.amount          || b?.pricing?.total  || 0;
const getVendorName    = (b) => b?.vendor?.name    || b?.vendor_name     || 'N/A';
// ─────────────────────────────────────────────────────────────────────────────

const BookingsNew = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [dateRange, setDateRange] = useState('all');

  // Approve / Reject dialog state
  const [approveDialog, setApproveDialog] = useState({ open: false, booking: null, loading: false, result: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, booking: null, loading: false, reason: '' });
  
  // Mobile detection for Lite view
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchBookings();
  }, []);

  // Smart default — when the page loads and there ARE pending requests,
  // jump straight to "Requires Confirmation" so the vendor lands on the
  // tab that actually needs their action. If everything's already
  // approved/cancelled/upcoming, leave the user on "All".
  const hasAutoTabbedRef = React.useRef(false);
  useEffect(() => {
    if (loading || hasAutoTabbedRef.current || activeTab !== 'all') return;
    const pending = (bookings || []).filter((b) => b.status === 'pending').length;
    if (pending > 0) {
      hasAutoTabbedRef.current = true;
      setActiveTab('requires_confirmation');
    } else if (!loading) {
      hasAutoTabbedRef.current = true;  // user has no pending — don't auto-jump on later refetches
    }
  }, [loading, bookings, activeTab]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/bookings/');
      setBookings(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load bookings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    const b = approveDialog.booking;
    if (!b) return;
    setApproveDialog(d => ({ ...d, loading: true }));
    try {
      const res = await apiCall(`/api/bookings/${b.id}/approve`, { method: 'PUT', body: JSON.stringify({}) });
      setApproveDialog(d => ({ ...d, loading: false, result: res }));
      fetchBookings();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to approve', variant: 'destructive' });
      setApproveDialog(d => ({ ...d, loading: false }));
    }
  };

  const handleReject = async () => {
    const b = rejectDialog.booking;
    if (!b) return;
    setRejectDialog(d => ({ ...d, loading: true }));
    try {
      await apiCall(`/api/bookings/${b.id}/reject`, { method: 'PUT', body: JSON.stringify({ reason: rejectDialog.reason }) });
      toast({ title: 'Rejected', description: 'Booking request rejected. Customer notified.' });
      setRejectDialog({ open: false, booking: null, loading: false, reason: '' });
      fetchBookings();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'Failed to reject', variant: 'destructive' });
      setRejectDialog(d => ({ ...d, loading: false }));
    }
  };

  // Map booking status to tabs
  const getBookingTab = (booking) => {
    const status = booking.status?.toLowerCase();
    const rawDate = getBookingDate(booking);
    const bookingDate = rawDate ? new Date(rawDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (status === 'pending') return 'requires_confirmation';
    if (status === 'rejected' || status === 'cancelled') return 'cancelled';
    if (status === 'confirmed' && bookingDate && bookingDate >= today) return 'upcoming';
    if (status === 'completed' || (status === 'confirmed' && bookingDate && bookingDate < today)) return 'completed';
    if (status === 'confirmed') return 'upcoming'; // confirmed with no date → upcoming

    return 'all';
  };

  // Filter by tab
  const filterByTab = (bookingsList) => {
    if (activeTab === 'all') return bookingsList;
    
    return bookingsList.filter(booking => getBookingTab(booking) === activeTab);
  };

  // Apply additional filters
  const applyFilters = (bookingsList) => {
    let filtered = [...bookingsList];
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.booking_number?.toLowerCase().includes(q) ||
        getCustomerName(booking).toLowerCase().includes(q) ||
        getServiceName(booking).toLowerCase().includes(q) ||
        getCustomerEmail(booking).toLowerCase().includes(q)
      );
    }
    
    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.created_at || booking.booking_date);
        const diffDays = Math.floor((now - bookingDate) / (1000 * 60 * 60 * 24));
        
        if (dateRange === 'today') return diffDays === 0;
        if (dateRange === 'week') return diffDays <= 7;
        if (dateRange === 'month') return diffDays <= 30;
        return true;
      });
    }
    
    return filtered;
  };

  // Sort bookings
  const sortBookings = (bookingsList) => {
    const sorted = [...bookingsList];
    
    switch (sortBy) {
      case 'date_desc':
        return sorted.sort((a, b) => new Date(b.created_at || b.booking_date) - new Date(a.created_at || a.booking_date));
      case 'date_asc':
        return sorted.sort((a, b) => new Date(a.created_at || a.booking_date) - new Date(b.created_at || b.booking_date));
      case 'customer':
        return sorted.sort((a, b) => getCustomerName(a).localeCompare(getCustomerName(b)));
      case 'booking_id':
        return sorted.sort((a, b) => (a.booking_number || '').localeCompare(b.booking_number || ''));
      case 'price_desc':
        return sorted.sort((a, b) => getAmount(b) - getAmount(a));
      case 'price_asc':
        return sorted.sort((a, b) => getAmount(a) - getAmount(b));
      default:
        return sorted;
    }
  };

  // Get display bookings with all filters and sorting
  const getDisplayBookings = useMemo(() => {
    const tabFiltered = filterByTab(bookings);
    const filtered = applyFilters(tabFiltered);
    return sortBookings(filtered);
  }, [bookings, activeTab, searchQuery, dateRange, sortBy]);

  // Get tab counts
  const getTabCounts = () => {
    return {
      all: bookings.length,
      requires_confirmation: bookings.filter(b => getBookingTab(b) === 'requires_confirmation').length,
      upcoming: bookings.filter(b => getBookingTab(b) === 'upcoming').length,
      completed: bookings.filter(b => getBookingTab(b) === 'completed').length,
      cancelled: bookings.filter(b => getBookingTab(b) === 'cancelled').length,
    };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending':   { label: 'Pending',   className: 'bg-yellow-100 text-yellow-800' },
      'confirmed': { label: 'Confirmed', className: 'bg-green-100 text-green-800' },
      'completed': { label: 'Completed', className: 'bg-blue-100 text-blue-800' },
      'cancelled': { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
      'rejected':  { label: 'Rejected',  className: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status?.toLowerCase()] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/export/csv`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bookings.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'Bookings exported successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export bookings',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading bookings...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 max-w-full overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Bookings</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Manage service and rental bookings</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} className="flex-1 md:flex-none">
              <Download className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => navigate('/bookings/new')} className="flex-1 md:flex-none">
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Create</span>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Card className="max-w-full overflow-hidden">
          <CardContent className="p-3 md:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="overflow-x-auto -mx-3 md:mx-0 px-3 md:px-0 pb-2">
                <TabsList className="inline-flex md:grid md:grid-cols-5 md:w-full gap-1">
                <TabsTrigger value="all" className="flex items-center gap-1 text-xs whitespace-nowrap px-3 py-2">
                  <Calendar className="h-4 w-4" />
                  All ({getTabCounts().all})
                </TabsTrigger>
                <TabsTrigger value="requires_confirmation" className="flex items-center gap-1 text-xs whitespace-nowrap px-3 py-2">
                  <AlertCircle className="h-4 w-4" />
                  Requires Confirmation ({getTabCounts().requires_confirmation})
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="flex items-center gap-1 text-xs whitespace-nowrap px-3 py-2">
                  <Clock className="h-4 w-4" />
                  Upcoming ({getTabCounts().upcoming})
                </TabsTrigger>
                <TabsTrigger value="completed" className="flex items-center gap-1 text-xs whitespace-nowrap px-3 py-2">
                  <CheckCircle className="h-4 w-4" />
                  Completed ({getTabCounts().completed})
                </TabsTrigger>
                <TabsTrigger value="cancelled" className="flex items-center gap-1 text-xs whitespace-nowrap px-3 py-2">
                  <XCircle className="h-4 w-4" />
                  Cancelled ({getTabCounts().cancelled})
                </TabsTrigger>
              </TabsList>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Filters and Search - Hidden on mobile (Lite Mode) */}
        <Card className={`${isMobile ? 'hidden' : ''}`}>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <Label className="text-sm mb-2 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Booking #, Customer, Service..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Date Range */}
              <div>
                <Label className="text-sm mb-2 block">Date Range</Label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort By */}
              <div>
                <Label className="text-sm mb-2 block">Sort By</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date_desc">Newest First</SelectItem>
                    <SelectItem value="date_asc">Oldest First</SelectItem>
                    <SelectItem value="customer">Customer Name</SelectItem>
                    <SelectItem value="booking_id">Booking ID</SelectItem>
                    <SelectItem value="price_desc">Highest Amount</SelectItem>
                    <SelectItem value="price_asc">Lowest Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Table - Desktop */}
        <Card className="hidden md:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-4 font-semibold">Booking #</th>
                    <th className="text-left p-4 font-semibold">Customer</th>
                    {user?.role === 'admin' && <th className="text-left p-4 font-semibold">Vendor</th>}
                    <th className="text-left p-4 font-semibold">Service</th>
                    <th className="text-left p-4 font-semibold">Schedule</th>
                    <th className="text-left p-4 font-semibold">Amount</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-left p-4 font-semibold">Last Updated</th>
                    <th className="text-left p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={user?.role === 'admin' ? '9' : '8'} className="text-center p-8 text-gray-500">
                        Loading bookings...
                      </td>
                    </tr>
                  ) : getDisplayBookings.length === 0 ? (
                    <tr>
                      <td colSpan={user?.role === 'admin' ? '9' : '8'} className="text-center p-8 text-gray-500">
                        No bookings found
                      </td>
                    </tr>
                  ) : (
                    getDisplayBookings.map((booking) => (
                      <tr
                        key={booking.id}
                        className={`border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                          booking.status === 'pending' ? 'bg-amber-50/60 border-l-4 border-l-amber-400' : ''
                        }`}
                        onClick={() => navigate(`/bookings/${booking.id}`)}
                        data-testid={`booking-row-${booking.id}`}
                      >
                        <td className="p-4 font-medium text-blue-600">
                          {booking.booking_number || booking.id?.substring(0, 8)}
                          {booking.booking_type === 'request' && (
                            <Badge
                              className="ml-2 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold"
                              data-testid={`booking-request-badge-${booking.id}`}
                            >
                              REQUEST
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <div>
                            <div className="font-medium">{getCustomerName(booking)}</div>
                            <div className="text-sm text-gray-500">{getCustomerEmail(booking)}</div>
                          </div>
                        </td>
                        {user?.role === 'admin' && (
                          <td className="p-4">
                            <div className="font-medium text-purple-600">{getVendorName(booking)}</div>
                          </td>
                        )}
                        <td className="p-4">
                          <div className="font-medium">{getServiceName(booking)}</div>
                          <div className="text-sm text-gray-500">{booking.booking_type || 'Request'}</div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-gray-400" />
                              {getBookingDate(booking) || 'N/A'}
                            </div>
                            {getBookingTime(booking) && (
                              <div className="flex items-center gap-1 text-gray-500">
                                <Clock className="h-3 w-3" />
                                {getBookingTime(booking)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 font-semibold">
                          ₹{getAmount(booking).toFixed ? getAmount(booking).toFixed(2) : Number(getAmount(booking)).toFixed(2)}
                        </td>
                        <td className="p-4">
                          {getStatusBadge(booking.status)}
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <div>{formatDate(booking.updated_at || booking.created_at)}</div>
                            <div className="text-xs text-gray-500">
                              Created {formatDate(booking.created_at)}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); navigate(`/bookings/${booking.id}`); }}
                              data-testid={`view-booking-${booking.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {booking.lead_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                onClick={(e) => { e.stopPropagation(); navigate(`/orders/leads?highlight=${booking.lead_id}`); }}
                                data-testid={`view-lead-from-booking-${booking.id}`}
                                title="View as lead (billing + analytics)"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                            {booking.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={(e) => { e.stopPropagation(); setApproveDialog({ open: true, booking, loading: false, result: null }); }}
                                  data-testid={`approve-booking-${booking.id}`}
                                  title="Approve"
                                >
                                  <ThumbsUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={(e) => { e.stopPropagation(); setRejectDialog({ open: true, booking, loading: false, reason: '' }); }}
                                  data-testid={`reject-booking-${booking.id}`}
                                  title="Reject"
                                >
                                  <ThumbsDown className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Cards - Mobile */}
        <div className="md:hidden">
          {isMobile ? (
            <Card>
              <CardContent className="p-0">
                <BookingsListLite 
                  bookings={getDisplayBookings.map(b => ({
                    ...b,
                    booking_date: getBookingDate(b),
                    booking_time: getBookingTime(b),
                    service_name: getServiceName(b),
                    customer_name: getCustomerName(b)
                  }))}
                  loading={loading}
                  onApprove={(booking) => setApproveDialog({ open: true, booking, loading: false, result: null })}
                  onReject={(booking) => setRejectDialog({ open: true, booking, loading: false, reason: '' })}
                  onCheckIn={async (booking) => {
                    try {
                      await apiCall(`/api/bookings/${booking.id}/status`, {
                        method: 'PUT',
                        body: JSON.stringify({ status: 'in_progress' })
                      });
                      toast({ title: 'Checked In', description: `Booking ${booking.booking_number || booking.id?.slice(-6)} is now in progress` });
                      fetchBookings();
                    } catch (error) {
                      toast({ title: 'Error', description: 'Failed to check in', variant: 'destructive' });
                    }
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            /* Fallback for non-mobile (shouldn't render due to md:hidden) */
            <div className="space-y-3">
              {getDisplayBookings.map((booking) => (
                <Card 
                  key={booking.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/bookings/${booking.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-blue-600 text-sm">
                          {booking.booking_number || booking.id?.substring(0, 8)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(booking.updated_at || booking.created_at)}
                        </p>
                      </div>
                      {getStatusBadge(booking.status)}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Customer</span>
                        <span className="text-sm font-medium">{getCustomerName(booking)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Service</span>
                        <span className="text-sm">{getServiceName(booking)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Amount</span>
                        <span className="text-sm font-semibold">₹{Number(getAmount(booking)).toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Approve Dialog ── */}
      <Dialog open={approveDialog.open} onOpenChange={(o) => !o && setApproveDialog({ open: false, booking: null, loading: false, result: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-700">Approve Booking Request</DialogTitle>
          </DialogHeader>
          {!approveDialog.result ? (
            <>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Customer:</span> {approveDialog.booking?.customer_name || approveDialog.booking?.customer?.name}</p>
                <p><span className="font-medium">Service:</span> {approveDialog.booking?.listing_title || approveDialog.booking?.service?.name}</p>
                {approveDialog.booking?.event_date && <p><span className="font-medium">Date:</span> {approveDialog.booking.event_date}</p>}
                {approveDialog.booking?.guests && <p><span className="font-medium">Guests:</span> {approveDialog.booking.guests}</p>}
                <p><span className="font-medium">Amount:</span> ₹{(approveDialog.booking?.amount || approveDialog.booking?.pricing?.total || 0).toLocaleString()}</p>
              </div>
              <p className="text-sm text-gray-600 mt-2">A Razorpay payment link will be generated and emailed to the customer.</p>
              <DialogFooter className="gap-2 mt-4">
                <Button variant="outline" onClick={() => setApproveDialog({ open: false, booking: null, loading: false, result: null })}>Cancel</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleApprove} disabled={approveDialog.loading} data-testid="confirm-approve-btn">
                  {approveDialog.loading ? 'Approving...' : 'Approve & Send Payment Link'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-700 font-semibold"><CheckCircle className="h-5 w-5" /> Booking Approved!</div>
              {approveDialog.result.payment_link && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Razorpay Payment Link:</p>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                    <span className="text-xs text-blue-600 truncate flex-1">{approveDialog.result.payment_link}</span>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(approveDialog.result.payment_link); sonnerToast('Link copied!'); }}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" asChild><a href={approveDialog.result.payment_link} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>
                  </div>
                  <p className="text-xs text-gray-500">Payment link emailed to the customer.</p>
                </div>
              )}
              {approveDialog.result.whatsapp_url && (
                <div>
                  <p className="text-sm font-medium mb-1">Send via WhatsApp:</p>
                  <Button variant="outline" className="w-full border-green-500 text-green-700 hover:bg-green-50" asChild>
                    <a href={approveDialog.result.whatsapp_url} target="_blank" rel="noopener noreferrer">
                      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.133.554 4.133 1.52 5.867L0 24l6.317-1.494A11.947 11.947 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.924 0-3.71-.52-5.24-1.42l-.378-.222-3.75.886.919-3.65-.245-.387A9.818 9.818 0 012.182 12C2.182 6.582 6.582 2.182 12 2.182S21.818 6.582 21.818 12 17.418 21.818 12 21.818z"/></svg>
                      Send via WhatsApp
                    </a>
                  </Button>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setApproveDialog({ open: false, booking: null, loading: false, result: null })}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Reject Dialog ── */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, booking: null, loading: false, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Reject Booking Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              The customer will be notified that you're unable to accept this request.
            </p>
            <div>
              <Label className="text-sm">Reason (optional)</Label>
              <Input
                placeholder="e.g. Date not available, fully booked..."
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
                data-testid="reject-reason-input"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, booking: null, loading: false, reason: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={rejectDialog.loading} data-testid="confirm-reject-btn">
              {rejectDialog.loading ? 'Rejecting...' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BookingsNew;
